<script setup lang="ts">
import { computed } from 'vue'
import { formatDate } from "@/services/functions"
import ResizableList from '@/components/ResizableList.vue';
import DisplayFullnameFromUpdatedBy from '@/components/Common/DisplayFullnameFromUpdatedBy.vue';
import i18n from '@/lang/i18n';

const ID = 'id'
const ACCOUNT_NAME = 'account_name'
const OFFICEABLE_TYPE = 'officeable_type'
const OFFICEABLE_NAME = 'officeable_name'
const UPDATED_AT = 'updated_at'
const UPDATED_BY = 'updated_by'

const ID_LABEL_KEY = 'pages.allowAccessOffice.list.id'
const ACCOUNT_NAME_LABEL_KEY = 'pages.allowAccessOffice.accountName'
const OFFICEABLE_TYPE_LABEL_KEY = 'pages.allowAccessOffice.list.officeableType'
const OFFICEABLE_NAME_LABEL_KEY = 'pages.allowAccessOffice.list.officeableName'
const ROLE_LABEL_KEY = 'pages.allowAccessOffice.list.role'
const DRIVER_ID_LABEL_KEY = 'pages.allowAccessOffice.list.driverId'
const UPDATED_AT_LABEL_KEY = 'pages.allowAccessOffice.list.updatedAt'
const UPDATED_BY_LABEL_KEY = 'pages.allowAccessOffice.list.updatedBy'
const EDIT_LABEL_KEY = 'pages.allowAccessOffice.list.edit'

const emits = defineEmits(['changeSortOrder', 'openModal'])

const props = withDefaults(
  defineProps<{
    selfRoleId: number
    accounts: Array<any>
    isLoading: boolean
    isSearchCompleted: boolean
    isManagementOffice: boolean
  }>(),
  {
    selfRoleId: 2,
    isLoading: true,
  }
)

const headerLabelKeys = [
  ID_LABEL_KEY,
  ACCOUNT_NAME_LABEL_KEY,
  OFFICEABLE_TYPE_LABEL_KEY,
  OFFICEABLE_NAME_LABEL_KEY,
  ROLE_LABEL_KEY,
  DRIVER_ID_LABEL_KEY,
  UPDATED_AT_LABEL_KEY,
  UPDATED_BY_LABEL_KEY,
  EDIT_LABEL_KEY
]
const header = computed(() => headerLabelKeys.map((labelKey) => ({
  labelKey,
  label: i18n.global.t(labelKey)
})))
const sortableItems: any = {
  [ID_LABEL_KEY]: ID,
  [ACCOUNT_NAME_LABEL_KEY]: ACCOUNT_NAME,
  [OFFICEABLE_TYPE_LABEL_KEY]: OFFICEABLE_TYPE,
  [OFFICEABLE_NAME_LABEL_KEY]: OFFICEABLE_NAME,
  [UPDATED_AT_LABEL_KEY]: UPDATED_AT,
  [UPDATED_BY_LABEL_KEY]: UPDATED_BY
}
const currentSort: any = { item: 'id', type: 'ASC' }
const sortTypes: any = ['ASC', 'DESC']
const officeableTypeNames: any = {
  ManagementOffice: i18n.global.t('pages.allowAccessOffice.list.managementOffice'),
  SubManagementOffice: i18n.global.t('pages.allowAccessOffice.list.subManagementOffice'),
  OperationOffice: i18n.global.t('pages.allowAccessOffice.operationOffice'),
  InstallationOffice: i18n.global.t('pages.allowAccessOffice.list.installationOffice')
}
const roles: any = {
  1: i18n.global.t('pages.allowAccessOffice.list.administrator'),
  2: i18n.global.t('pages.allowAccessOffice.list.driver')
}
const lastElementIndex = 2

function datetimeFormat(datetime: any) {
  const d = new Date(datetime)
  return formatDate(d)
}

function officeableTypeName(officeableType: any) {
  const type = officeableType.split('\\')[lastElementIndex]
  return officeableTypeNames[type]
}

function officeableName(account: any) {
  const type = account.officeable_type.split('\\')[lastElementIndex]
  if (type == 'OperationOffice') {
    return account.officeable.operation_office_name
  } else {
    return account.officeable.company_name
  }
}

function emitChangeSortOrder(labelKey: string, sortType: any = null) {
  if (!sortableItem(labelKey)) return

  const item: any = sortableItems[labelKey]
  if (sortType) {
    currentSort.type = sortType
  } else {
    if (currentSort.item == item) {
      const type: any = sortTypes.find((type: any) => type != currentSort.type)
      currentSort.type = type
    } else {
      currentSort.type = sortTypes[0]
    }
  }
  currentSort.item = item
  emits('changeSortOrder', currentSort.item, currentSort.type)
}

function sortableItem(labelKey: string) {
  return Object.keys(sortableItems).includes(labelKey)
}

function addClassIfSortable(labelKey: string) {
  if (!sortableItem(labelKey)) return

  return 'sort-icon pl-2 pt-1'
}

function highLightIfCurrentSort(labelKey: string, type: string) {
  if (currentSort.item != sortableItems[labelKey] || currentSort.type != type) return

  return 'fw-bold'
}

function applyLeftClass(index: number) {
  if (index == 0) return 'start-0'
}

function openModal(account: any | null) {
  emits('openModal', account)
}
</script>
<template>
  <div class="allow-access-office-list">
    <template v-if="isLoading">
      <div class="list-loading">
        <label>{{ i18n.global.t('pages.common.list.reading') }}</label>
      </div>
    </template>
    <template v-else-if="!isSearchCompleted && isManagementOffice">
      <div class="list-loading">
        <label>{{ i18n.global.t('pages.common.list.runFilter') }}</label>
      </div>
    </template>
    <ResizableList
      scrollAreaSelector=".list"
    >
      <div class="list list-scrollbar">
        <v-table>
          <thead>
            <tr class="table-header-row">
              <th v-for="(item, index) in header" :key="item.labelKey" v-show="item.labelKey != EDIT_LABEL_KEY || selfRoleId == 1" class="table-header column-space" :class="applyLeftClass(index)">
                <div class="d-flex">
                  <span v-if="sortableItem(item.labelKey)" class="d-inline-block">
                    <div class="nc-icon nc-stre-up sort-icon d-block" @click="emitChangeSortOrder(item.labelKey, sortTypes[0])" :class="highLightIfCurrentSort(item.labelKey, sortTypes[0])" />
                    <div class="nc-icon nc-stre-down sort-icon d-block" @click="emitChangeSortOrder(item.labelKey, sortTypes[1])" :class="highLightIfCurrentSort(item.labelKey, sortTypes[1])" />
                  </span>
                  <span :class="addClassIfSortable(item.labelKey)" @click="emitChangeSortOrder(item.labelKey)">{{ item.label }}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="accounts?.length">
              <tr v-for="account in accounts" :key="account.id">
                <td class="column-space fix-td">{{account.id}}</td>
                <td class="column-space">{{ account.last_name + ' ' + account.first_name }}</td>
                <td class="column-space">{{ officeableTypeName(account.officeable_type) }}</td>
                <td class="column-space">{{ officeableName(account) }}</td>
                <td class="column-space">{{ roles[account.role_id] }}</td>
                <td class="column-space">{{ account.driver_id }}</td>
                <td class="column-space">{{ datetimeFormat(account.updated_at) }}</td>
                <td class="column-space">
                  <DisplayFullnameFromUpdatedBy
                    :updatedBy="account.updated_by"
                  />
                </td>
                <td v-show="selfRoleId == 1" class="column-space">
                  <div class="material-symbols-outlined edit-icon" @click="openModal(account)">edit_note</div>
                </td>
              </tr>
            </template>
            <template v-else-if="!accounts?.length && !isLoading && isSearchCompleted">
              <tr>
                <td colspan="9">
                  {{ i18n.global.t('pages.common.list.noData') }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </div>
    </ResizableList>
  </div>
</template>
<style>
.allow-access-office-list .list table {
  position: absolute;
}
.allow-access-office-list .list td {
  word-break: keep-all;
}
.allow-access-office-list .list th {
  min-width: 7em;
  position: -webkit-sticky;
  position: sticky;
  background-color: white;
  top: 0;
  z-index: 1;
}
</style>
<style scoped>
.allow-access-office-list {
  position: relative;
}
.allow-access-office-list .list {
  position: relative;
  min-width: 58vw;
  height: var(--resizable-height, var(--list-default-height));
  overflow: auto;
}
.allow-access-office-list table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
}
.allow-access-office-list .list .id-column {
  min-width: 2em;
}
.allow-access-office-list .list .updated-at-column {
  min-width: 10em;
}
.checkbox-in-grid {
  padding: 1vm;
  height: inherit !important;
  display: inline-grid !important;
}
.list-loading {
  position: absolute;
  min-width: 58vw;
  height: 100%;
  overflow: auto;
  z-index: 1000;
  background-color: rgba(80,80,80,0.5);
  right: 0;
  left: 0;
}
.list-loading>label {
  font-weight: bold;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}
.column-space {
  white-space: nowrap;
}
.edit-icon:hover {
  cursor: pointer;
  opacity: 0.5;
}
.allow-access-office-list .sort-icon:hover {
  cursor: pointer;
  opacity: 0.5;
}
</style>
